import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

//
// Fork 14 (2026-04-17) — SMS verify page.
//
// Consumes the { mfa_required, mfa_channel:'sms', mfa_pending_token,
// masked_phone } flow stashed in AuthContext.pendingMfa after
// POST /auth/login. Posts the 6-digit code to /auth/verify-totp (legacy
// route name; the backend delegates to verifySmsCode when the user has a
// phone on file — see totp-routes.js:233). The backend sets the HttpOnly
// butler_session cookie on success; we re-probe /auth/me via AuthContext
// so isAuthenticated flips true in the same tick as our navigate call.
//
// Visual language mirrors LoginCard so the session feels continuous:
// Butler marquee palette + MagicMoney admin-portal card chrome + Work
// Sans / Montserrat type pairing. A lighter top-border accent (cyan
// instead of midway-blue) signals the "verify" step without a full
// color swap.
//
export function VerifySmsPage() {
  const { pendingMfa, verifyMfa, clearPendingMfa } = useAuth()
  const navigate = useNavigate()
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus the code field on mount so the OS autofill sheet lines up with
  // the SMS notification (iOS auto-fill needs the input focused to
  // surface the code chip above the keyboard).
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Route guard — if the challenge expired while this page was mounted
  // (user tabbed away, came back past the 5-min TTL), bounce cleanly
  // back to /login.
  if (!pendingMfa) {
    return <Navigate to="/login" replace />
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const trimmed = code.trim()
    if (trimmed.length < 6) {
      setError('Enter the 6-digit code.')
      return
    }
    setError(null)
    setLoading(true)
    const result = await verifyMfa(trimmed)
    setLoading(false)
    if (!result.ok) {
      setError(result.error ?? 'Verification failed')
      if (result.expired) {
        // Bounce to /login after the error has a moment to paint — the
        // pendingMfa cleanup inside verifyMfa will switch the guard on
        // the next navigation anyway, but explicit navigate keeps the
        // URL tidy.
        navigate('/login', { replace: true })
      }
      return
    }
    navigate('/home', { replace: true })
  }

  function handleCancel() {
    clearPendingMfa()
    navigate('/login', { replace: true })
  }

  const canSubmit = !loading && code.trim().length >= 6
  const deliveryFailed = pendingMfa.channel === 'sms' && pendingMfa.smsSent === false

  // Codex P2 (Fork 14 review): backup codes only exist for TOTP-enrolled
  // accounts, which Fork 11 disabled enrollment for. Telling an SMS-only
  // user to "enter a backup code" is a dead end — the only real recovery
  // is to re-login (Twilio may be transiently down) or contact an admin
  // (account has no phone on file at all). Show a retry-focused UI
  // instead of the phantom code field.
  if (deliveryFailed) {
    return (
      <div className="min-h-screen relative flex items-center justify-center bg-butler-lobby dark:bg-slate-900 px-4 py-10 overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-70 dark:opacity-40"
          style={{
            background:
              'radial-gradient(1200px 600px at 20% 10%, rgba(0,199,202,.18), transparent 55%), radial-gradient(900px 500px at 85% 90%, rgba(237,0,190,.12), transparent 60%), radial-gradient(800px 500px at 50% 110%, rgba(255,246,98,.18), transparent 60%)',
          }}
        />
        <div className="relative w-full max-w-md">
          <div className="flex flex-col items-center text-center mb-8">
            <div className="mm-card flex items-center justify-center p-2 mb-5 shadow-card-soft">
              <img
                src="/butler/butler-logo.png"
                alt="Butler Amusements — carousel horse logo"
                className="h-20 w-auto"
              />
            </div>
            <h1 className="font-display text-3xl font-bold text-butler-700 dark:text-butler-100 tracking-tight">
              Couldn&rsquo;t send your code
            </h1>
            <p
              className="mt-1 text-sm text-ink-soft dark:text-slate-300"
              data-testid="mfa-destination"
            >
              We couldn&rsquo;t reach {pendingMfa.maskedPhone ?? 'the phone on file'} just now.
              Try signing in again in a moment, or contact your Butler Buddy admin if this
              keeps happening.
            </p>
          </div>

          <div className="mm-card rounded-panel p-6 space-y-4 border-t-4 border-marquee-600 dark:bg-slate-800 dark:border-marquee-400">
            <button
              type="button"
              onClick={handleCancel}
              className="w-full rounded-card bg-butler-600 hover:bg-butler-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-butler-500 text-white font-semibold uppercase tracking-[0.16em] py-3 transition-all shadow-[0_8px_20px_rgba(90,120,255,.25)]"
            >
              Back to sign in
            </button>
          </div>

          <p className="mt-6 text-center text-xs text-ink-soft dark:text-slate-400">
            <span className="marquee-dot mr-2 align-middle" />
            Need help? Reach out to your MagicMoney account lead.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen relative flex items-center justify-center bg-butler-lobby dark:bg-slate-900 px-4 py-10 overflow-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-70 dark:opacity-40"
        style={{
          background:
            'radial-gradient(1200px 600px at 20% 10%, rgba(0,199,202,.18), transparent 55%), radial-gradient(900px 500px at 85% 90%, rgba(237,0,190,.12), transparent 60%), radial-gradient(800px 500px at 50% 110%, rgba(255,246,98,.18), transparent 60%)',
        }}
      />

      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-8">
          <div className="mm-card flex items-center justify-center p-2 mb-5 shadow-card-soft">
            <img
              src="/butler/butler-logo.png"
              alt="Butler Amusements — carousel horse logo"
              className="h-20 w-auto"
            />
          </div>
          <h1 className="font-display text-3xl font-bold text-butler-700 dark:text-butler-100 tracking-tight">
            Verify your phone
          </h1>
          <p
            className="mt-1 text-sm text-ink-soft dark:text-slate-300"
            data-testid="mfa-destination"
          >
            {deliveryFailed
              ? 'We couldn\u2019t send a code to your phone right now.'
              : pendingMfa.maskedPhone
              ? `We texted a 6-digit code to ${pendingMfa.maskedPhone}.`
              : 'We texted a 6-digit code to the phone on file.'}
          </p>
          <div className="mt-3 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-butler-600 dark:text-butler-200 font-semibold">
            <span className="h-px w-6 bg-hairline" />
            <span>Second factor</span>
            <span className="h-px w-6 bg-hairline" />
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="mm-card rounded-panel p-6 space-y-4 border-t-4 border-marquee-600 dark:bg-slate-800 dark:border-marquee-400"
          aria-label="SMS verification form"
        >
          <div>
            <label
              htmlFor="mfa-code"
              className="block text-xs font-semibold uppercase tracking-[0.12em] text-ink-soft dark:text-slate-300 mb-2"
            >
              6-digit code
            </label>
            <input
              ref={inputRef}
              id="mfa-code"
              name="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={10}
              required
              value={code}
              onChange={(e) => {
                // Keep digits-only for the default SMS path so iOS
                // auto-fill slots cleanly. Backup codes are also numeric
                // on Butler Buddy (generateBackupCodes produces 8-digit
                // numerics — see totp.js), so digit-only stays correct
                // for the fallback.
                const next = e.target.value.replace(/\D/g, '')
                setCode(next)
              }}
              className="w-full rounded-card border border-hairline-soft dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2.5 text-ink dark:text-white placeholder:text-ink-soft focus:outline-none focus:ring-2 focus:ring-butler-500 focus:border-butler-500 transition-colors font-mono tracking-[0.4em] text-center text-lg"
              placeholder="••••••"
              aria-invalid={error ? 'true' : 'false'}
              aria-describedby={error ? 'mfa-err' : undefined}
            />
          </div>

          {error && (
            <div
              id="mfa-err"
              role="alert"
              className="text-sm text-marquee-700 bg-marquee-50 dark:bg-marquee-900/20 dark:text-marquee-100 rounded-card border border-marquee-200 dark:border-marquee-700 px-3 py-2"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full rounded-card bg-butler-600 hover:bg-butler-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-butler-500 text-white font-semibold uppercase tracking-[0.16em] py-3 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_8px_20px_rgba(90,120,255,.25)]"
          >
            {loading ? 'Verifying…' : 'Verify'}
          </button>

          <button
            type="button"
            onClick={handleCancel}
            className="w-full text-xs font-semibold uppercase tracking-[0.16em] text-ink-soft hover:text-butler-600 dark:text-slate-300 dark:hover:text-butler-200 transition-colors"
          >
            Sign in as a different user
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-ink-soft dark:text-slate-400">
          <span className="marquee-dot mr-2 align-middle" />
          Butler Buddy keeps MFA codes in memory only — never stored.
        </p>
      </div>
    </div>
  )
}
